import { ComparisonType, ComparisonValueType } from "@schema-types/types/JourneySchema";
import {
  isRelativeDateComparisonValue,
  resolveBranchRuleDateComparisonToIso,
} from "@schema-types/branchRuleDateComparison";

export function evaluateRule(
  value: any,
  comparisonValue: any,
  comparisonType: ComparisonType,
  comparisonValueType: ComparisonValueType,
  evaluationTimeZoneId?: string | null
): boolean {
  switch (comparisonValueType) {
    case ComparisonValueType.String:
      return evaluateStringComparison(value, comparisonValue, comparisonType);
    case ComparisonValueType.Integer:
    case ComparisonValueType.Decimal:
      return evaluateNumericComparison(value, comparisonValue, comparisonType);
    case ComparisonValueType.Boolean:
      return evaluateBooleanComparison(value, comparisonValue, comparisonType);
    case ComparisonValueType.Date:
      return evaluateDateComparison(value, comparisonValue, comparisonType, evaluationTimeZoneId);
    case ComparisonValueType.StringArray:
      return evaluateStringArrayComparison(value, comparisonValue, comparisonType);
    default:
      return false;
  }
}

function evaluateStringComparison(value: any, comparisonValue: any, comparisonType: ComparisonType): boolean {
  const stringValue = String(value ?? "");
  const stringComparisonValue = String(comparisonValue ?? "");

  switch (comparisonType) {
    case ComparisonType.Equals:
      return stringValue.toLowerCase() === stringComparisonValue.toLowerCase();
    case ComparisonType.NotEquals:
      return stringValue.toLowerCase() !== stringComparisonValue.toLowerCase();
    case ComparisonType.Contains:
      return stringValue.toLowerCase().includes(stringComparisonValue.toLowerCase());
    case ComparisonType.GreaterThan:
    case ComparisonType.LessThan:
      return false; // Not valid for strings
    default:
      return false;
  }
}

function evaluateNumericComparison(value: any, comparisonValue: any, comparisonType: ComparisonType): boolean {
  const numValue = Number(value);
  const numComparisonValue = Number(comparisonValue);

  if (isNaN(numValue) || isNaN(numComparisonValue)) {
    return false;
  }

  switch (comparisonType) {
    case ComparisonType.Equals:
      return numValue === numComparisonValue;
    case ComparisonType.NotEquals:
      return numValue !== numComparisonValue;
    case ComparisonType.GreaterThan:
      return numValue > numComparisonValue;
    case ComparisonType.LessThan:
      return numValue < numComparisonValue;
    case ComparisonType.Contains:
      return false; // Not valid for numbers
    default:
      return false;
  }
}

function evaluateBooleanComparison(value: any, comparisonValue: any, comparisonType: ComparisonType): boolean {
  if (value !== true && value !== false) {
    return false;
  }
  const boolComparisonValue = Boolean(comparisonValue);

  switch (comparisonType) {
    case ComparisonType.Equals:
      return value === boolComparisonValue;
    case ComparisonType.NotEquals:
      return value !== boolComparisonValue;
    case ComparisonType.Contains:
    case ComparisonType.GreaterThan:
    case ComparisonType.LessThan:
      return false; // Not valid for booleans
    default:
      return false;
  }
}

function normalizeAnswerDateToIso(value: any): string | null {
  if (typeof value !== "string" || value.length < 10) {
    return null;
  }
  const head = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(head) ? head : null;
}

function evaluateDateComparison(
  value: any,
  comparisonValue: any,
  comparisonType: ComparisonType,
  evaluationTimeZoneId?: string | null
): boolean {
  const valueIso = normalizeAnswerDateToIso(value);
  const comparisonIso = resolveBranchRuleDateComparisonToIso(comparisonValue, evaluationTimeZoneId);

  if (!valueIso || !comparisonIso) {
    return false;
  }

  if (isRelativeDateComparisonValue(comparisonValue)) {
    switch (comparisonType) {
      case ComparisonType.Equals:
        return valueIso === comparisonIso;
      case ComparisonType.NotEquals:
        return valueIso !== comparisonIso;
      case ComparisonType.GreaterThan:
        return valueIso < comparisonIso;
      case ComparisonType.LessThan:
        return valueIso > comparisonIso;
      case ComparisonType.Contains:
        return false;
      default:
        return false;
    }
  }

  switch (comparisonType) {
    case ComparisonType.Equals:
      return valueIso === comparisonIso;
    case ComparisonType.NotEquals:
      return valueIso !== comparisonIso;
    case ComparisonType.GreaterThan:
      return valueIso > comparisonIso;
    case ComparisonType.LessThan:
      return valueIso < comparisonIso;
    case ComparisonType.Contains:
      return false; // Not valid for dates
    default:
      return false;
  }
}

function evaluateStringArrayComparison(value: any, comparisonValue: any, comparisonType: ComparisonType): boolean {
  if (!Array.isArray(comparisonValue)) {
    return false;
  }

  let valueArray: string[];
  if (Array.isArray(value)) {
    valueArray = value.map(String);
  } else if (typeof value === "string") {
    valueArray = [value];
  } else {
    valueArray = [];
  }

  const comparisonArray = comparisonValue.map(String);

  switch (comparisonType) {
    case ComparisonType.Equals:
      return compareArrays(valueArray, comparisonArray);
    case ComparisonType.NotEquals:
      return !compareArrays(valueArray, comparisonArray);
    case ComparisonType.Contains:
      return comparisonArray.some((cv) => valueArray.includes(cv));
    case ComparisonType.GreaterThan:
    case ComparisonType.LessThan:
      return false; // Not valid for arrays
    default:
      return false;
  }

  function compareArrays(array1: string[], array2: string[]): boolean {
    return (
      array1.length === array2.length &&
      array1.sort().every((item, index) => item === array2.sort()[index])
    );
  }
}
